import { useState } from "react"
import { useForm } from "@inertiajs/react"
import { IconPlus } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/components/ui/responsive-modal"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface NewDeviceDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  showTrigger?: boolean
}

export function NewDeviceDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showTrigger = false,
}: NewDeviceDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? controlledOnOpenChange! : setInternalOpen

  const { data, setData, post, processing, errors, reset } = useForm({
    name: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/devices", {
      onSuccess: () => {
        setOpen(false)
        reset()
      },
    })
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      reset()
    }
  }

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <ResponsiveModalTrigger asChild>
          <Button size="sm" aria-label="Add Device">
            <IconPlus className="size-4" />
            <span className="hidden md:inline">Add Device</span>
          </Button>
        </ResponsiveModalTrigger>
      )}
      <ResponsiveModalContent>
        <form onSubmit={handleSubmit}>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Add New Device</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Create a new device to monitor. You'll receive an API token for remote reporting.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <div className="grid gap-4 px-4 py-4 sm:px-0">
            <div className="grid gap-2">
              <Label htmlFor="name">Device Name</Label>
              <Input
                id="name"
                placeholder="My Tilt Hydrometer"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                autoComplete="off"
                data-1p-ignore
                autoFocus
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>
          </div>
          <ResponsiveModalFooter>
            <Button type="submit" disabled={processing || !data.name.trim()}>
              Create Device
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
